import { UIAsset } from '../config/assets';
import { UIIcon } from '../config/icons';
import { ABPaymentForm } from '../lib/ab/payment';
import { ABScreen } from '../lib/ab/screen';
import { blkRenderResource } from '../lib/blk/render';
import { aTrackPage } from '../lib/mdl/a-track';
import { CPlan } from '../lib/mdl/c-plan';
import { CPaidPlanId, cUserFullName } from '../lib/mdl/c-user';
import { EPaymentIntent } from '../lib/mdl/e-payment-intent';
import { PGiftcardPlans } from '../lib/mdl/p-giftcard';
import { pGiftcardB2B, pGiftcardBuy, pGiftcardCheck, pGiftcardPlansGet, pGiftcardPlansWithCode } from '../lib/mdl/p-giftcard-home';
import { meAction } from '../lib/me/home';
import { the_login_scope, the_user } from '../lib/me/login';
import { uiRegisterAnchor } from '../lib/ui/anchor';
import { uiSetBG } from '../lib/ui/bg';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UINumberInput, UITextInput } from '../lib/ui/component/input';
import { hLine } from '../lib/ui/component/line';
import { UISelect } from '../lib/ui/component/select';
import { uiToast } from '../lib/ui/component/toast';
import { UIExclusiveToggles, UIToggle, UIToggleButton } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { uiMakeI18NValues } from '../lib/ui/functions';
import { UIScreen } from '../lib/ui/screen';
import { UIValue } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { $analytics } from '../lib/y/analytics';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { $i18n, the_language, yGetI18N, yGetI18Ns, yInterpolate, YLanguage } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { HomeMenu } from './menu';

function makeDeliveryButton(v: UIValue<string>) {
    const card = $el.DIV('r-card', $el.ICON(UIIcon.CHECK, 'r-checkmark'));
    uiSetBG(card, { media: v.key == 'digital' ? UIAsset.GIFTCARD_DIGITAL : UIAsset.GIFTCARD_POST });

    return new UIToggle($el.DIV('tl-card k-img-choose', [
        card,
        $el.DIV('r-subtitle', v.name),
        $el.DIV('r-info', v.key == 'digital' ? $i18n.tl.t_giftcard_delivery_email : $i18n.tl.t_giftcard_delivery_post)
    ]), { key: v.key })
}

class PromoDialog extends UIDialog<PGiftcardPlans> {
    constructor() {
        super({ close: true, classes: 'm-550' })

        const code = new UITextInput({ required: true, placeholder: $i18n.tl.ph_voucher_code, autofocus: true });

        this.content.ui_add([
            $el.DIV('w-block l-form m-center', [
                $el.H2('title', $i18n.tl.t_promo_code),
                code,
                $el.DIV('l-button-row', [
                    new UIButton({
                        title: $i18n.tl.b_apply_code, classes: 'ui-tint-reverse', on_click: () => {
                            if(!$form.validate(code)) return;

                            const code_value = code.value;

                            pGiftcardPlansWithCode(code_value).then(response => {
                                if(!response.code) {
                                    uiToast($i18n.tl.e_invalid_code);
                                    return;
                                }
                                this.dismiss(response);
                            }, uiToast);
                        }
                    })
                ])
            ])
        ]);
    }
}

class ChoosePage extends UIView {
    constructor(plans: PGiftcardPlans, callback: (data: any) => any, screen: UIScreen) {
        super();

        const months_values: UIValue<1 | 2 | 3 | 6 | 12>[] = [{ key: 1, name: '1' }, { key: 2, name: '2' }, { key: 3, name: '3' }, { key: 6, name: '6' }, { key: 12, name: '12' }];
        const months_toggle = new UIExclusiveToggles(months_values, v => new UIToggleButton({ kind: 'k-shadow', classes: 'm-toggle', title: v.name, key: v.key }), { required: true, classes: 'b b-content l-button-row m-center' });
        months_toggle.value = 1;

        const quantity_values = [1, 2, 3, 4, 5];
        const quantity_toggle = new UIExclusiveToggles(quantity_values, v => new UIToggleButton({ kind: 'k-shadow', classes: 'm-toggle', title: v + '', key: v }), { required: true, classes: 'b b-content l-button-row m-center' });
        quantity_toggle.value = 1;

        const plan_toggle = new UIExclusiveToggles<CPlan, CPaidPlanId>(plans.plans, plan => {
            const months = months_toggle.value || 1;
            const price = plans.pricing[plan.id][months]
            return new UIToggle($el.DIV('o-card k-horizontal m-checkable m-shadow m-border', [
                $el.DIV('r-title', yGetI18N(plan.name)),
                hLine(),
                $el.DIV('r-price', price.value / 100 + ' ' + price.currency),
                $el.UL(yGetI18Ns(plan.points)),
                new UIButton({ title: $i18n.tl.b_membership_choose, kind: 'k-shadow', click_through: true }),
            ]), { key: plan.id });
        }, { required: true, classes: 'blk-card-chooser w-block b b-content' });
        plan_toggle.value = plans.plans[0].id;

        const delivery_values = uiMakeI18NValues($i18n.tl.o_giftcard_delivery, ['digital', 'post']);
        const delivery_toggle = new UIExclusiveToggles(delivery_values, v => makeDeliveryButton(v), { required: true, classes: 'blk-card-chooser w-block b b-content' });

        const buyer_name = new UITextInput({ placeholder: $i18n.tl.ph_name, required: true });
        const buyer_email = new UITextInput({ type: 'email', placeholder: $i18n.tl.ph_email, required: true });

        const language_values: UIValue<YLanguage>[] = [{ key: 'sv', name: 'Svenska' }, { key: 'en', name: 'English' }, { key: 'de', name: 'Deutsch' }];
        const language = new UISelect(language_values, { placeholder: $i18n.tl.h_language, required: true });
        language.value = the_language;

        const buyer_fields = $el.FORM('l-form m-center', [
            $el.H3('new-heading', $i18n.tl.h_giftcard_contact),
            buyer_name,
            buyer_email,
            language,
            $el.DIV('s-medium')
        ]);
        buyer_fields.ui_hide();

        if(the_user) {
            buyer_name.value = cUserFullName(the_user);
            buyer_email.value = the_user.email!;
        }

        const address_name = new UITextInput({ placeholder: $i18n.tl.ph_name, required: true });
        const address_address = new UITextInput({ placeholder: $i18n.tl.ph_address, required: true });
        const address_post_code = new UITextInput({ placeholder: $i18n.tl.ph_postcode, required: true });
        const address_city = new UITextInput({ placeholder: $i18n.tl.ph_postcity, required: true });

        const address_fields = $el.FORM('l-form m-center', [
            $el.H3('new-heading', $i18n.tl.h_giftcard_delivery_address),
            address_name,
            address_address,
            address_post_code,
            address_city,
            $el.DIV('s-medium')
        ]);
        address_fields.ui_hide();

        const confirm = new UIButton({
            title: $i18n.tl.b_giftcard_confirm_info, kind: 'k-shadow', classes: 'ui-tint-blue', on_click: () => {
                if(!form.validate()) return;
                const value = form.value;
                if(delivery_toggle.value == 'post') {
                    if(!address_form.validate()) return;
                    value.address = address_form.value;
                }
                value.codes = plans.code ? [plans.code] : window.ab_codes;
                value.country = window.y_default_country; // TODO: if it's not default country we're sending, then it should be getCountry
                return callback(value);
            }
        });

        confirm.ui_hide();

        const discount_text = $el.P('new-info', '', 'p-a-center');

        const code_button = new UIButton({
            title: $i18n.tl.b_have_code, kind: 'k-text', on_click: () => {
                new PromoDialog().present().then(p => {
                    plans = p;
                    recalcDiscount();
                    plan_toggle.reload();
                    code_button.ui_hide();
                })
            }
        })

        if(plans.code)
            code_button.ui_hide();

        const order_section = $el.SECTION('pad-t-wide pad-b-wide w-block ui-tint-red c-light', [
            $el.P('new-title', $i18n.tl.h_giftcard_months, 'p-a-center'),
            months_toggle,
            $el.DIV('s-spread'),
            $el.P('new-title', $i18n.tl.h_giftcard_quantity, 'p-a-center'),
            quantity_toggle,
            $el.DIV('s-spread'),
            $el.P('new-title', $i18n.tl.h_giftcard_kind, 'p-a-center'),
            plan_toggle,
            discount_text,
            $el.DIV('l-button-row t-sz-large', code_button),
            $el.DIV('s-spread'),
            plans.post && [
                $el.P('new-title', $i18n.tl.h_giftcard_delivery, 'p-a-center'),
                $el.DIV('tl-options-block', delivery_toggle),
                $el.DIV('s-medium')
            ],
            $el.DIV('w-block-400 l-form m-center', [
                buyer_fields,
                address_fields,
                confirm
            ])
        ]);
        uiRegisterAnchor(order_section, 'order');

        const recalcDiscount = () => {
            const m = months_toggle.value;
            if(m) {
                const discount = Math.min(Math.round(100 * (1 - plans.pricing.plusone[m].value / m / plans.base_price.plusone.value)), Math.round(100 * (1 - plans.pricing.standard[m].value / m / plans.base_price.standard.value)))
                discount_text.textContent = discount > 0 ? yInterpolate($i18n.tl.t_giftcard_discount, discount) : '';
            }
        };

        $on(months_toggle, 'value', _m => {
            recalcDiscount();
            plan_toggle.reload();
        });

        $on(delivery_toggle, 'value', key => {
            buyer_fields.ui_show();
            confirm.ui_show();
            if(key == 'post')
                address_fields.ui_show();
            else
                address_fields.ui_hide();
        });

        if(!plans.post)
            delivery_toggle.value = 'digital';

        const address_form = $form({ name: address_name, address: address_address, post_code: address_post_code, city: address_city });
        const form = $form({ months: months_toggle, quantity: quantity_toggle, plan: plan_toggle, delivery: delivery_toggle, buyer_name, buyer_email, language });

        blkRenderResource('giftcard', screen).then(content => {
            this.ui_add([
                content,
                order_section
            ]);
        });

        recalcDiscount();
    }
}

class B2BPage extends UIView {
    constructor(plans: PGiftcardPlans, callback: (data: any) => any, screen: UIScreen) {
        super();

        const months_values: UIValue<3 | 6 | 12>[] = [{ key: 3, name: '3 months' }, { key: 6, name: '6' }, { key: 12, name: '12' }];
        const months_toggle = new UIExclusiveToggles(months_values, v => new UIToggleButton({ kind: 'k-shadow', classes: 'm-toggle', title: v.name, key: v.key }), { required: true, classes: 'b b-content l-button-row m-center' });
        months_toggle.value = 3;

        const plan_toggle = new UIExclusiveToggles<CPlan, CPaidPlanId>(plans.plans, plan => {
            return new UIToggle($el.DIV('o-card k-horizontal m-checkable m-shadow m-border', [
                $el.DIV('r-title', yGetI18N(plan.name)),
                hLine(),
                $el.UL(yGetI18Ns(plan.points)),
                new UIButton({ title: $i18n.tl.b_membership_choose, kind: 'k-shadow', classes: 'ui-tint-red', click_through: true }),
            ]), { key: plan.id });
        }, { required: true, classes: 'blk-card-chooser w-block b b-content' });
        plan_toggle.value = plans.plans[0].id;

        const contact_name = new UITextInput({ placeholder: $i18n.tl.ph_name, required: true });
        const contact_email = new UITextInput({ type: 'email', placeholder: $i18n.tl.ph_email, required: true });
        const contact_company = new UITextInput({ placeholder: $i18n.tl.ph_company_name, required: true });
        const contact_number = new UINumberInput({ placeholder: $i18n.tl.ph_gc_amount, required: true });

        const form = $form({
            months: months_toggle, kind: plan_toggle, name: contact_name, email: contact_email, company: contact_company,
            quantity: contact_number
        });

        const order_section = $el.SECTION('pad-t-wide pad-b-wide w-block ui-tint-red c-light', [
            $el.P('new-title', $i18n.tl.h_giftcard_months, 'p-a-center'),
            months_toggle,
            $el.DIV('s-spread'),
            $el.P('new-title', $i18n.tl.h_giftcard_kind_2, 'p-a-center'),
            plan_toggle,
            $el.DIV('s-section'),
            $el.DIV('w-block-400 l-form m-center', [
                $el.FORM('l-form m-center', [
                    $el.H3('new-heading', $i18n.tl.h_giftcard_contact),
                    contact_name,
                    contact_email,
                    contact_company,
                    contact_number,
                    $el.DIV('s-medium'),
                ]),
                new UIButton({
                    title: "Intresseanmälan", kind: 'k-shadow', classes: 'ui-tint-red', on_click: () => {
                        if(!form.validate()) return;
                        return callback(form.value);
                    }
                }),
                new UIButton({ title: "Läs mer", kind: 'k-text', classes: 'ui-tint-blue', link: 'https://support.abundolive.se/sv/articles/6637010-en-upplevelserik-julklapp' })
            ])
        ]);

        uiRegisterAnchor(order_section, 'order');

        blkRenderResource('giftcard_b2b', screen).then(content => {
            this.ui_add([
                content,
                order_section
            ]);
        });
    }
}

class PayPage extends UIView {
    constructor(intent: EPaymentIntent, callback: () => void) {
        super();

        this.ui_add($el.DIV('ui-section pad-t-wide pad-b-wide w-block bg-lightgreen c-lgreen', [
            $el.DIV('l-form m-center', [
                $el.P('title', $i18n.tl.h_payment),
                $el.P('input-hint', $i18n.tl.t_giftcard_payment),
                $el.DIV('s-tiny'),
                new ABPaymentForm(intent, { finish: callback })
            ])
        ]));
    }
}

class ConfirmationPage extends UIView {
    constructor(screen: UIScreen) {
        super();

        blkRenderResource('giftcard_confirmation', screen).then(content => {
            this.ui_add(content);
        });
    }
}

class B2BConfirmationPage extends UIView {
    constructor(screen: UIScreen) {
        super();

        blkRenderResource('giftcard_b2b_confirmation', screen).then(content => {
            this.ui_add(content);
        });
    }
}

export class HomeGiftcardScreen extends ABScreen {
    constructor() {
        super({ menu: new HomeMenu({ classes: 'm-white c-light' }) });
    }

    protected override async fillContent(_content: UIViewSpec) {
        const plans = await pGiftcardPlansGet();

        //TODO: do something like fillContent but for pages
        this.replaceContent(new ChoosePage(plans, data => {
            return pGiftcardBuy(data).then(intent => {
                this.replaceContent(new PayPage(intent, () => {
                    this.replaceContent(new ConfirmationPage(this));
                    $analytics.track('Gift Card Purchase', { revenue: intent.amount.value / 100, currency: intent.amount.currency, delivery: data.delivery });
                }));
                $analytics.track('Gift Card Purchase Started', { delivery: data.delivery });
            })
        }, this));

        aTrackPage('giftcard');
    }
}

export class HomeGiftcardB2BScreen extends ABScreen {
    constructor() {
        super({ menu: new HomeMenu({ classes: 'm-white c-light' }) });
    }

    protected override fillContent(_content: UIViewSpec): void {
        pGiftcardPlansGet().then(plans => {
            this.replaceContent(new B2BPage(plans, data => {
                return pGiftcardB2B(data).then(() => {
                    this.replaceContent(new B2BConfirmationPage(this));
                    $analytics.track('b2b lead', data);
                });
            }, this));
        })

        aTrackPage('giftcard_b2b');
    }
}

export class HomeRedeemCodeScreen extends ABScreen {
    constructor(private _fill_code?: string) {
        super({ menu: new HomeMenu({ classes: 'm-white c-light' }) });
    }

    protected override fillContent(content: UIViewSpec): void {
        const fill_code = this._fill_code;

        const code = new UITextInput({ placeholder: $i18n.tl.ph_gc_code, autofill: 'giftcard-code' });
        if(fill_code)
            code.value = fill_code;
        const read_more = $el.A('/', $i18n.tl.b_read_more_abundo);

        const redeem = new UIButton({
            title: $i18n.tl.b_enter_gc, kind: 'k-shadow', on_click: () => {
                if(the_login_scope) {
                    return meAction('redeem_gc', { code: code.value }).then(() => { $location.navigate([]) }, uiToast);
                } else {
                    const gc_code = code.value;
                    return pGiftcardCheck(gc_code).then(() => {
                        $location.navigate(['signup'], { origin: 'footer', gc_code: gc_code });
                    }, uiToast);
                }
            }
        });

        content.ui_add($el.SECTION('pad-t-wide pad-b-wide w-block ui-tint-blue c-light', [
            $el.H3('new-heading', $i18n.tl.h_redeem_gc, 'w-block-700 p-a-center'),
            $el.DIV('w-block-400 l-form m-center', [
                code,
                redeem,
                !the_login_scope && read_more
            ])
        ]));

        aTrackPage('redeem_code');
    }
}
